import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { SearchResultsGroupMember } from 'features/searchMolecules/searchMoleculesSlice';
import zipObject from 'lodash/zipObject';

// Types

export type Extents = { [name: string]: { min: number; max: number } };

export type FilterValues = [number, number];

export type CalcFilter = {
  values: FilterValues;
  lowerChanged: boolean;
  upperChanged: boolean;
};

export interface FilteringState {
  [name: string]: CalcFilter;
}

export interface FilterChangePayload {
  name: string;
  newValues: FilterValues;
}

export interface InitialisePayload {
  activeCalcs: string[];
  selectedResultMembers: SearchResultsGroupMember[];
}

// Untility functions

export const getExtentsOf = (
  members: SearchResultsGroupMember[],
  activeCalcs: string[],
): Extents => {
  if (!activeCalcs.length) return {};
  const ranges = activeCalcs.map((name) => {
    const values = members
      .map((member) => member.calculations[name])
      .filter((v) => v !== undefined);

    return { min: Math.min(...values), max: Math.max(...values) };
  });

  return zipObject(activeCalcs, ranges);
};

export const getSlidersExtents = (
  members: SearchResultsGroupMember[],
  activeCalcs: string[],
  filters: FilteringState,
) => {
  const slidersExtents: Extents = {};
  const extents = getExtentsOf(members, activeCalcs);
  activeCalcs.forEach((name) => {
    const {
      [name]: { values },
    } = filters;

    slidersExtents[name] = {
      min: Math.min(extents[name].min, values[0]),
      max: Math.max(extents[name].max, values[1]),
    };
  });
  return slidersExtents;
};

// Slice

const initialState: FilteringState = {};

export const resultFilteringSlice = createSlice({
  name: 'resultFiltering',
  initialState,
  reducers: {
    initialiseFilters(state, action: PayloadAction<InitialisePayload>) {
      const { activeCalcs, selectedResultMembers } = action.payload;
      const extents = getExtentsOf(selectedResultMembers, activeCalcs);

      if (selectedResultMembers.length) {
        for (let name of activeCalcs) {
          const {
            [name]: { min, max },
          } = extents;
          if (name in state) {
            const filter = state[name];
            filter.values = [
              filter.lowerChanged ? filter.values[0] : min,
              filter.upperChanged ? filter.values[1] : max,
            ];
          } else {
            state[name] = {
              values: [min, max],
              lowerChanged: false,
              upperChanged: false,
            };
          }
        }
      }
    },
    changeFilter(state, action: PayloadAction<FilterChangePayload>) {
      const { name, newValues } = action.payload;
      const { values } = state[name];
      if (newValues[0] !== values[0]) {
        state[name].lowerChanged = true;
      }
      if (newValues[1] !== values[1]) {
        state[name].upperChanged = true;
      }
      state[name].values = newValues;
    },
    clearFilters(state) {
      Object.keys(state).forEach((key) => {
        delete state[key];
      });
    },
  },
});

// Selectors

// get the auth slice form the RootState
const resultFilteringStateSelector = (state: RootState, ..._: any): FilteringState =>
  state.resultFiltering;

export const getFilters = createSelector(resultFilteringStateSelector, (state) => state);
export const getCalcFilter = createSelector(
  resultFilteringStateSelector,
  (_: RootState, name: string) => name,
  (state, name) => state[name],
);
export const getCalcHasChanged = createSelector(getCalcFilter, (calcFilter) => [
  calcFilter.lowerChanged,
  calcFilter.upperChanged,
]);
export const getFilterCalcs = createSelector(resultFilteringStateSelector, (state) =>
  Object.keys(state),
);

export const { initialiseFilters, changeFilter, clearFilters } = resultFilteringSlice.actions;

// Extract the action creators object and the reducer
export const { actions, reducer } = resultFilteringSlice;

// Export the reducer, either as a default or named export
export default resultFilteringSlice.reducer;
